import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Os primitivos visuais do Console, do layout entregue em 17/09/2026.
 *
 * Eles existem em UM lugar porque a alternativa e a classe do card escrita a
 * mao em 24 telas. A primeira aplicacao do layout ja mostrou o custo disso pelo
 * avesso: a casca ficou no desenho novo e as telas ficaram no antigo, porque
 * nao havia nada compartilhado entre elas. Vinte e quatro copias divergem na
 * segunda semana, e a que envelhece e sempre a menos usada.
 *
 * As medidas (`--r: 15px`, a sombra, o gradiente) saem do `:root` do mockup,
 * e estao nos tokens do Tailwind: `rounded-card`, `shadow-card`, `bg-card`.
 */

/** O cartao do desenho: gradiente sutil, borda de 1px, raio 15 e sombra. */
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-card border border-line bg-card p-4 shadow-card ${className}`.trim()}>
      {children}
    </div>
  );
}

/**
 * Titulo de painel: pequeno, em caixa alta, com espacamento de letra.
 *
 * Recebe `id` porque a secao que o contem aponta para ele com
 * `aria-labelledby`: painel sem nome acessivel e um bloco anonimo para quem
 * navega por leitor de tela, e o desenho usa esses rotulos justamente como a
 * ancora visual de cada bloco.
 */
export function TituloDePainel({
  id,
  texto,
  className = '',
}: {
  id: string;
  texto: string;
  className?: string;
}) {
  return (
    <h2
      id={id}
      className={`text-xs font-semibold uppercase tracking-wider text-ink-muted ${className}`.trim()}
    >
      {texto}
    </h2>
  );
}

/** O "Ver todas →" do desenho, no canto do painel. */
export function LinkDePainel({ para, texto }: { para: string; texto: string }) {
  return (
    <Link
      to={para}
      className="shrink-0 rounded-[8px] border border-line px-2 py-1 text-xs text-ink-muted hover:bg-surface-raised hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent"
    >
      {texto} →
    </Link>
  );
}

/** Cabecalho de painel com titulo a esquerda e acao opcional a direita. */
export function CabecalhoDePainel({
  id,
  texto,
  para,
  acao,
}: {
  id: string;
  texto: string;
  para?: string;
  acao?: string;
}) {
  return (
    <div className="mb-3 flex items-start justify-between gap-2">
      <TituloDePainel id={id} texto={texto} />
      {para !== undefined && acao !== undefined && <LinkDePainel para={para} texto={acao} />}
    </div>
  );
}

/**
 * Etiqueta de estado, o `.tag` do mockup.
 *
 * O TEXTO e obrigatorio e a cor acompanha, nunca substitui: o handoff do pacote
 * exige "nao depender apenas de cor", e uma bolinha verde sozinha nao diz nada
 * em monocromatico nem para quem nao distingue as duas cores.
 */
export type TomDeEtiqueta = 'neutro' | 'ok' | 'warn' | 'danger' | 'accent';

const TOM: Record<TomDeEtiqueta, string> = {
  neutro: 'border-line bg-surface-sunken text-ink-muted',
  ok: 'border-ok-border bg-ok-surface text-ok-ink',
  warn: 'border-warn-border bg-warn-surface text-warn-ink',
  danger: 'border-danger-border bg-danger-surface text-danger-ink',
  accent: 'border-accent-border bg-accent-soft text-accent-ink',
};

export function Etiqueta({
  tom = 'neutro',
  children,
  testId,
}: {
  tom?: TomDeEtiqueta;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <span
      className={`inline-block shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TOM[tom]}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

/**
 * A barra de progresso do mockup (`.progress`).
 *
 * Medidas do desenho: trilho de 8 px, raio 20, e o preenchimento com o
 * gradiente verde. Ela existia no desenho desde o primeiro dia e eu nao a tinha
 * feito; o dono apontou.
 *
 * `fracao` e um numero de 0 a 1, e a barra so aparece quando ha um valor REAL
 * para mostrar. Barra em 0% seria lida como "zero", e ausencia de medida nao e
 * zero: quem chama passa `null` e nao desenha nada.
 */
export function Barra({
  fracao,
  rotulo,
  testId,
}: {
  fracao: number;
  rotulo: string;
  testId?: string;
}) {
  const pct = Math.max(0, Math.min(1, fracao)) * 100;
  return (
    <div
      className="mt-2.5 h-2 overflow-hidden rounded-full bg-master-trilho"
      role="progressbar"
      aria-label={rotulo}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      data-testid={testId}
    >
      {/* Largura por estilo: a classe do Tailwind teria de existir literal. */}
      <i className="block h-full rounded-full bg-barra" style={{ width: `${pct}%` }} />
    </div>
  );
}
